import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import RefSiteFooter from "./RefSiteFooter";
import RefPricingTeaser from "./RefPricingTeaser";

// Assets
import contact_us from "../../Assets/headers/contact_us.png";

function RefContact() {
  const { t } = useTranslation('contact');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    firstname: Yup.string().required(t('validation.firstNameRequired')),
    lastname: Yup.string().required(t('validation.lastNameRequired')),
    businessemail: Yup.string()
      .email(t('validation.emailInvalid'))
      .required(t('validation.emailRequired')),
    companyname: Yup.string().required(t('validation.companyNameRequired')),
    comments: Yup.string(),
  });

  const triggerConfetti = () => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    const colors = ["#25B2E8", "#1662A7", "#7A5AF8", "#00C2A8", "#F59E0B"];
    const pieces = 140;
    for (let i = 0; i < pieces; i += 1) {
      const confetti = document.createElement("div");
      const size = Math.random() * 8 + 4;
      const startX = Math.random() * window.innerWidth;
      const drift = (Math.random() - 0.5) * 320;
      const rotate = Math.random() * 1080;
      const duration = 1800 + Math.random() * 1400;
      confetti.style.position = "fixed";
      confetti.style.left = `${startX}px`;
      confetti.style.top = "-16px";
      confetti.style.width = `${size}px`;
      confetti.style.height = `${size * 0.6}px`;
      confetti.style.background =
        colors[Math.floor(Math.random() * colors.length)];
      confetti.style.borderRadius = "2px";
      confetti.style.zIndex = "9999";
      confetti.style.pointerEvents = "none";
      confetti.style.opacity = "1";
      confetti.style.transform = `translate3d(0, 0, 0) rotate(0deg)`;
      confetti.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity ${duration}ms linear`;
      document.body.appendChild(confetti);
      requestAnimationFrame(() => {
        confetti.style.transform = `translate3d(${drift}px, ${window.innerHeight + 100}px, 0) rotate(${rotate}deg)`;
        confetti.style.opacity = "0";
      });
      setTimeout(() => confetti.remove(), duration + 100);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      businessemail: "",
      companyname: "",
      comments: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        const response = await fetch(
          "https://server.requiresign.com/send-contact-form",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: values.firstname,
              lastName: values.lastname,
              email: values.businessemail,
              companyName: values.companyname,
              comments: values.comments,
            }),
          },
        );
        if (response.ok) {
          setIsSubmitted(true);
          triggerConfetti();
        } else {
          throw new Error("Submission failed");
        }
      } catch (err) {
        setStatus({
          type: "error",
          message: t('form.submitError'),
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="rco-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
        :root {
          --rco-ink: #163A5A; --rco-ink2:#000000; --rco-ink3: #5E87A3;
          --rco-sky: #1A63A5; --rco-mint: #2AB6E8;
          --rco-secondary: #2AB6E8;
          --rco-secondary-deep: #1AA3D4;
          --rco-secondary-soft: #D9F4FC;
          --rco-secondary-muted: rgba(42,182,232,0.18);
          --rco-sky-light: #E8F4FC;
          --rco-sky-hover: #155a94;
          --rco-bg-dark: #0E2D48;
          --rco-white: #FFFFFF; --rco-surface: #F5F9FC;
          --rco-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rco-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
        }
        .rco-page { font-family: var(--rco-ff-body); color: var(--rco-ink); background: var(--rco-white); }

        .rco-hero {
          padding: 54px 24px 0px;
          background: linear-gradient(180deg, #E8F4FC 0%, var(--rco-secondary-soft) 35%, #fff 100%);
          position: relative; overflow: hidden;
          text-align: center;
        }
        .rco-hero::before {
          content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
          width: 900px; height: 500px;
          background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
          pointer-events: none;
        }
        .rco-hero-content { max-width: 800px; margin: 0 auto; position: relative; z-index: 1; }
        
        .rco-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, var(--rco-sky-light) 0%, var(--rco-secondary-soft) 100%);
          color: var(--rco-sky); border: 1px solid var(--rco-secondary-muted);
          font-size: 12px; font-weight: 600; padding: 8px 16px;
          border-radius: 100px; margin-bottom: 24px; letter-spacing: .06em;
          text-transform: uppercase;
          box-shadow: 0 4px 12px rgba(42,182,232,0.1);
        }
        .rco-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rco-secondary); animation: rco-pulse 2s infinite; }
        @keyframes rco-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.8)} }
        .rco-h1 { font-family: var(--rco-ff-head); font-size: 64px; font-weight: 800; letter-spacing: -.02em; line-height: 1.05; margin-bottom: 24px; color: var(--rco-ink); }
        .rco-h1-grad { background: linear-gradient(135deg, var(--rco-sky), var(--rco-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .rco-p { font-size: 18px; color: var(--rco-ink2); line-height: 1.6; margin: 0 auto 48px; max-width: 580px; }

        /* FORM SECTION */
        .rco-form-wrap { position: relative; padding: 64px 24px 120px; display: flex; justify-content: center; z-index: 2; }
        .rco-form-card {
          background: #fff; border-radius: 24px; border: 1px solid #E8EAF2;
          padding: 48px; box-shadow: 0 32px 64px rgba(22,58,90,0.12);
          width: 100%; max-width: 640px;
        }
        .rco-form-header { text-align: center; margin-bottom: 40px; }
        .rco-form-title { font-size: 32px; font-weight: 800; color: var(--rco-ink); margin-bottom: 12px; }
        .rco-form-sub { font-size: 16px; color: var(--rco-ink2); }

        .rco-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px 24px; }
        .rco-form-group { position: relative; display: flex; flex-direction: column; text-align: left; }
        .rco-form-group.full { grid-column: span 2; }
        
        .rco-label { 
          position: absolute; top: -8px; left: 16px; 
          background: #fff; padding: 0 6px; 
          font-size: 12px; font-weight: 500; color: var(--rco-ink2); 
          z-index: 2; transition: all 0.2s;
          pointer-events: none;
        }
        .rco-input {
          font-family: var(--rco-ff-body); font-size: 15px; padding: 16px 18px;
          border-radius: 12px; border: 1.5px solid #E8EAF2; background: transparent;
          transition: all 0.2s; outline: none; width: 100%; box-sizing: border-box;
          color: var(--rco-ink);
        }
        .rco-input:focus { border-color: var(--rco-sky); box-shadow: 0 0 0 4px rgba(26,99,165,0.05); }
        .rco-form-group:focus-within .rco-label { color: var(--rco-sky); }
        
        .rco-input.error { border-color: #EF4444; }
        .rco-error-msg { font-size: 12px; color: #EF4444; margin: 6px 0 0 4px; font-weight: 600; }

        .rco-textarea { min-height: 120px; resize: vertical; }

        .rco-submit-btn {
          width: 100%; margin-top: 12px; font-family: var(--rco-ff-body); font-size: 16px; font-weight: 600;
          padding: 18px; border-radius: 12px; border: none; cursor: pointer; transition: all 0.2s;
          background: linear-gradient(135deg, var(--rco-sky) 0%, var(--rco-secondary-deep) 100%);
          color: #fff; box-shadow: 0 8px 24px rgba(42,182,232,0.35);
        }
        .rco-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(42,182,232,0.45); }
        .rco-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        .rco-success { text-align: center; padding: 40px 0; }
        .rco-success-icon {
          width: 80px; height: 80px; background: #22C55E; color: #fff;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          font-size: 40px; margin: 0 auto 24px; box-shadow: 0 16px 32px rgba(34, 197, 94, 0.3);
        }

        @media (max-width: 1024px) {
          .rco-grid { grid-template-columns: 1fr; gap: 48px; }
          .rco-text-side { text-align: center; padding-top: 0; }
          .rco-p { margin: 0 auto 32px; }
          .rco-h1 { font-size: 48px; }
        }
        @media (max-width: 600px) {
          .rco-h1 { font-size: 40px; }
          .rco-form-card { padding: 32px 20px; }
          .rco-form-grid { grid-template-columns: 1fr; }
          .rco-form-group.full { grid-column: span 1; }
        }
      `}</style>

      {/* HERO SECTION WITH GRID */}
      <div className="rco-hero">
        <div className="rco-hero-content">
          <h1 className="rco-h1">
            {t('hero.titlePrefix')}<span className="rco-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rco-p">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      <div className="rco-form-wrap">
        <div className="rco-form-card">
          {isSubmitted ? (
            <div className="rco-success">
              <div className="rco-success-icon">✓</div>
              <h2 className="rco-form-title">{t('success.title')}</h2>
              <p className="rco-form-sub">
                {t('success.message')}
              </p>
              <button
                className="rco-submit-btn"
                style={{
                  marginTop: "32px",
                  width: "auto",
                  padding: "14px 40px",
                }}
                onClick={() => navigate("/")}
              >
                {t('success.backHome')}
              </button>
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <div className="rco-form-header">
                <h2 className="rco-form-title">{t('form.headerTitle')}</h2>
                <p className="rco-form-sub">
                  {t('form.headerSub')}
                </p>
              </div>

              {formik.status && (
                <div
                  style={{
                    background: "#FEE2E2",
                    color: "#B91C1C",
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "24px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {formik.status.message}
                </div>
              )}

              <div className="rco-form-grid">
                <div className="rco-form-group">
                  <input
                    name="firstname"
                    type="text"
                    className={`rco-input ${formik.touched.firstname && formik.errors.firstname ? "error" : ""}`}
                    {...formik.getFieldProps("firstname")}
                  />
                  <label className="rco-label">{t('form.firstNameLabel')}</label>
                  {formik.touched.firstname && formik.errors.firstname && (
                    <div className="rco-error-msg">
                      {formik.errors.firstname}
                    </div>
                  )}
                </div>

                <div className="rco-form-group">
                  <input
                    name="lastname"
                    type="text"
                    className={`rco-input ${formik.touched.lastname && formik.errors.lastname ? "error" : ""}`}
                    {...formik.getFieldProps("lastname")}
                  />
                  <label className="rco-label">{t('form.lastNameLabel')}</label>
                  {formik.touched.lastname && formik.errors.lastname && (
                    <div className="rco-error-msg">
                      {formik.errors.lastname}
                    </div>
                  )}
                </div>

                <div className="rco-form-group full">
                  <input
                    name="businessemail"
                    type="email"
                    className={`rco-input ${formik.touched.businessemail && formik.errors.businessemail ? "error" : ""}`}
                    {...formik.getFieldProps("businessemail")}
                  />
                  <label className="rco-label">{t('form.emailLabel')}</label>
                  {formik.touched.businessemail &&
                    formik.errors.businessemail && (
                      <div className="rco-error-msg">
                        {formik.errors.businessemail}
                      </div>
                    )}
                </div>

                <div className="rco-form-group full">
                  <input
                    name="companyname"
                    type="text"
                    className={`rco-input ${formik.touched.companyname && formik.errors.companyname ? "error" : ""}`}
                    {...formik.getFieldProps("companyname")}
                  />
                  <label className="rco-label">{t('form.companyNameLabel')}</label>
                  {formik.touched.companyname && formik.errors.companyname && (
                    <div className="rco-error-msg">
                      {formik.errors.companyname}
                    </div>
                  )}
                </div>

                <div className="rco-form-group full">
                  <textarea
                    name="comments"
                    className="rco-input rco-textarea"
                    {...formik.getFieldProps("comments")}
                  />
                  <label className="rco-label">
                    {t('form.commentsLabel')}
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="rco-submit-btn"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? t('form.sendingBtn') : t('form.sendBtn')}
              </button>
            </form>
          )}
        </div>
      </div>
      <RefPricingTeaser />
      <RefSiteFooter />
    </div>
  );
}

export default RefContact;
